import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, message, Space, Popconfirm, Tag } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, StopOutlined, UndoOutlined } from '@ant-design/icons';
import api from '../services/api';

const Instances = () => {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [teamsModalVisible, setTeamsModalVisible] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [editingInstance, setEditingInstance] = useState(null);
  const [instanceTeams, setInstanceTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [form] = Form.useForm();

  const fetchInstances = async () => {
    setLoading(true);
    try {
      const response = await api.get('/instances');
      setInstances(response.data);
    } catch (error) {
      message.error('Erro ao carregar instâncias');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchInstances();
  }, []);

  const handleSubmit = async (values) => {
    try {
      if (editingInstance) {
        await api.put(`/instances/${editingInstance.id}`, values);
        message.success('Instância atualizada com sucesso!');
      } else {
        await api.post('/instances', values);
        message.success('Instância criada com sucesso!');
      }
      setModalVisible(false);
      setEditingInstance(null);
      form.resetFields();
      fetchInstances();
    } catch (error) {
      message.error(editingInstance ? 'Erro ao atualizar instância' : 'Erro ao criar instância');
    }
  };

  const handleChangeStatus = async (id, newStatus) => {
    try {
      await api.put(`/instances/${id}/status`, { status: newStatus });
      const labels = {
        CANCELADO: 'Instância cancelada — não vai mais aparecer na sincronização dos times.',
        ATIVO: 'Instância reativada!',
        INATIVO: 'Instância inativada.',
      };
      message.success(labels[newStatus] || 'Status atualizado');
      fetchInstances();
    } catch (error) {
      message.error('Erro ao atualizar status');
    }
  };

  const showEditModal = (instance) => {
    setEditingInstance(instance);
    setModalVisible(true);
    form.setFieldsValue(instance);
  };

  const showTeams = async (instance) => {
    setSelectedInstance(instance);
    setTeamsLoading(true);
    setTeamsModalVisible(true);
    try {
      const response = await api.get(`/instances/${instance.id}/teams`);
      setInstanceTeams(response.data);
    } catch (error) {
      message.error('Erro ao carregar times da instância');
    }
    setTeamsLoading(false);
  };

  const getStatusTag = (status) => {
    const colors = {
      'ATIVO': 'green',
      'LIMITE_ATINGIDO': 'orange',
      'INATIVO': 'red',
      'CANCELADO': 'default',
    };
    return <Tag color={colors[status] || 'default'}>{status}</Tag>;
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Nome', dataIndex: 'name', key: 'name' },
    { title: 'Telefone', dataIndex: 'phone', key: 'phone' },
    { title: 'Data Cadastro', dataIndex: 'created_at', key: 'created_at', render: (date) => new Date(date).toLocaleDateString('pt-BR') },
    { title: 'Total Times', dataIndex: 'total_teams', key: 'total_teams' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: getStatusTag },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => showEditModal(record)} size="small" title="Editar" />
          <Button icon={<EyeOutlined />} onClick={() => showTeams(record)} size="small" title="Ver times" />
          {record.status === 'CANCELADO' ? (
            <Popconfirm
              title="Reativar esta instância?"
              description="Volta pra ATIVO e pode voltar a aparecer na sincronização."
              onConfirm={() => handleChangeStatus(record.id, 'ATIVO')}
              okText="Reativar"
              cancelText="Não"
            >
              <Button icon={<UndoOutlined />} size="small" title="Reativar" />
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Cancelar esta instância?"
              description="Não vai mais aparecer na tela de sincronização dos times. Dá pra reverter depois."
              onConfirm={() => handleChangeStatus(record.id, 'CANCELADO')}
              okText="Cancelar instância"
              cancelText="Voltar"
            >
              <Button icon={<StopOutlined />} danger size="small" title="Cancelar" />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const teamsColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    { title: 'Nome do Time', dataIndex: 'game_name', key: 'game_name' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: (status) => <Tag color={status === 'ATIVO' ? 'green' : 'red'}>{status}</Tag> },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Instâncias</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)}>
          Nova Instância
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={instances}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editingInstance ? 'Editar Instância' : 'Nova Instância'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingInstance(null);
          form.resetFields();
        }}
        footer={null}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item name="name" label="Nome" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Telefone" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item style={{marginTop: 28}}>
            <Space>
              <Button type="primary" htmlType="submit">{editingInstance ? 'Atualizar' : 'Salvar'}</Button>
              <Button onClick={() => {
                setModalVisible(false);
                setEditingInstance(null);
                form.resetFields();
              }}>Cancelar</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Times da Instância: ${selectedInstance?.name}`}
        open={teamsModalVisible}
        onCancel={() => setTeamsModalVisible(false)}
        footer={null}
        width={800}
      >
        <Table
          columns={teamsColumns}
          dataSource={instanceTeams}
          rowKey="id"
          loading={teamsLoading}
          pagination={{ pageSize: 10 }}
        />
      </Modal>
    </div>
  );
};

export default Instances;